import { useCallback, useEffect, useRef, useState } from 'react';

import { getAppPlazaList } from '@/pages/data-center/app-center/app-plaza/service';
import type { AppPlazaListItem } from '@/pages/data-center/app-center/app-plaza/types';
import { mapAppPlazaListItems } from '@/pages/data-center/app-center/app-plaza/utils';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

function filterItemsByKeyword(list: AppPlazaListItem[], keyword: string) {
  const key = keyword.trim().toLowerCase();
  if (!key) return list;
  return list.filter((item) => {
    const name = String(item.name || '').toLowerCase();
    const vendor = String(item.vendor || '').toLowerCase();
    const version = String(item.version || '').toLowerCase();
    return name.includes(key) || vendor.includes(key) || version.includes(key);
  });
}

/** 拉取应用广场列表（创建实例弹窗选择用，支持分页与搜索） */
export function useAppPlazaRepoList() {
  const [items, setItems] = useState<AppPlazaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const clientModeRef = useRef(false);
  const allItemsRef = useRef<AppPlazaListItem[]>([]);

  const applyClientPage = useCallback((list: AppPlazaListItem[], nextPage: number, nextLimit: number, nextKeyword: string) => {
    const filtered = filterItemsByKeyword(list, nextKeyword);
    const start = (nextPage - 1) * nextLimit;
    setItems(filtered.slice(start, start + nextLimit));
    setTotal(filtered.length);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const trimmedKeyword = searchKeyword.trim();

        if (clientModeRef.current && allItemsRef.current.length > 0) {
          if (cancelled) return;
          applyClientPage(allItemsRef.current, page, limit, searchKeyword);
          return;
        }

        const result = await getAppPlazaList({
          page,
          limit,
          artifactType: 'ai_application',
          ...(trimmedKeyword ? { 'name[like]': trimmedKeyword } : {}),
        });
        if (cancelled) return;

        const list = mapAppPlazaListItems(result.items);
        const totalItems = typeof result.totalItems === 'number' ? result.totalItems : list.length;
        const serverIgnoresPagination = page === 1 && !trimmedKeyword && list.length > limit && totalItems <= list.length;

        if (serverIgnoresPagination) {
          clientModeRef.current = true;
          allItemsRef.current = list;
          applyClientPage(list, page, limit, searchKeyword);
          return;
        }

        clientModeRef.current = false;
        allItemsRef.current = [];
        setItems(list);
        setTotal(totalItems);
      } catch (error) {
        console.error('fetch app plaza list failed', error);
        if (!cancelled) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyClientPage, limit, page, searchKeyword]);

  const onInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const onSearch = useCallback((value: string) => {
    clientModeRef.current = false;
    allItemsRef.current = [];
    setInputValue(value);
    setSearchKeyword(value.trim());
    setPage(DEFAULT_PAGE);
  }, []);

  const onPageChange = useCallback(
    (nextPage: number, nextPageSize?: number) => {
      setPage(nextPage);
      if (nextPageSize && nextPageSize !== limit) {
        setLimit(nextPageSize);
      }
    },
    [limit],
  );

  return {
    items,
    loading,
    page,
    limit,
    total,
    inputValue,
    onInputChange,
    onSearch,
    onPageChange,
  };
}
